
  import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import { useI18n } from 'vue-i18n/index';
  import { Actions } from '@/store/enums/StoreEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';

  export default defineComponent({
    name: 'promotions-listing',
    components: {
      Datatable,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (key) => (te(key) ? t(key) : key);

      const loading = ref(false);

      const tableHeader = ref([
        { name: translate('actions'), key: 'actions' },
        { name: translate('ID'), key: 'id', sortable: true },
        { name: translate('titleEn'), key: 'titleEn', sortable: true },
        { name: translate('titleAr'), key: 'titleAr', sortable: true },
        { name: translate('promoCode'), key: 'promoCode', sortable: true },
        { name: translate('packageId'), key: 'packageId', sortable: true },
        {
          name: translate('discountPercentage'),
          key: 'discountPercentage',
          sortable: true,
        },
        {
          name: translate('allowedUsage'),
          key: 'allowedUsage',
          sortable: true,
        },
        { name: translate('actualUsage'), key: 'actualUsage', sortable: true },
        { name: translate('duration'), key: 'duration', sortable: true },
        { name: translate('isActive'), key: 'isActive', sortable: true },
      ]);

      const fetchPromotions = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_PROMOTIONS);
        loading.value = false;
      };

      const editPromotion = (id: number) => {
        router.push({ name: 'promotion-editing', params: { id } });
      };

      const viewPromoHistory = async (id) => {
        router.push({ name: 'promotion-history', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const goTo = (pageName: string) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PROMOTIONS_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_PROMOTIONS);
        loading.value = false;
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('promotions'), [
          translate('subscriptions'),
        ]);
        await fetchPromotions();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_PROMOTION_STORE);
      });

      const toggleStatusPromotion = async (ads) => {
        const deletedMessage = ads.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_PROMOTION, ads.id);
          await store.dispatch(Actions.GET_PROMOTIONS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = ads.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      return {
        tableData: computed(() => store.getters.promotionsList),
        totalItems: computed(() => store.getters.promotionsListCount),
        tableHeader,
        loading,
        toggleStatusPromotion,
        translate,
        editPromotion,
        goTo,
        pageChanged,
        can,
        viewPromoHistory,
      };
    },
  });
